import React, { useState, useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ScatterChart, Scatter, ReferenceLine } from 'recharts';
import { RISK_CATEGORIES } from '../../constants/extremeEventsConfig';
import { PowerLawDistribution } from '../../utils/powerLawUtils';
import { formatCurrency, formatPercentage } from '../../utils/formatters';

/**
 * Power Law Distribution Visualizations Component
 * Phase 2: Power law plots, probability-impact analysis, and distribution modeling
 */
const PowerLawChartsSubtab = ({ 
    simulationResults,
    isDarkMode = false 
}) => {
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [chartScale, setChartScale] = useState('linear'); // 'linear' or 'log'

    // Generate theoretical power law curves for each category
    const powerLawData = useMemo(() => {
        const categories = {};
        
        Object.entries(RISK_CATEGORIES).forEach(([categoryId, category]) => {
            try {
                const { k, alpha } = category.powerLawParams;
                if (!k || !alpha || k <= 0 || alpha <= 0) {
                    console.warn(`Invalid power law parameters for category ${categoryId}:`, { k, alpha });
                    return;
                }
                const distribution = new PowerLawDistribution(k, alpha);
            
                // Generate data points from x = 0.1 to x = 10 (impact multipliers)
                const dataPoints = [];
                for (let x = 0.1; x <= 10; x += 0.1) {
                    const probability = distribution.pdf(x);
                    const cumulativeProbability = distribution.cdf(x);
                    
                    // Only add valid data points
                    if (isFinite(probability) && isFinite(cumulativeProbability) && probability > 0) {
                        dataPoints.push({
                            impactMultiplier: x,
                            probability,
                            cumulativeProbability,
                            logProbability: Math.log10(Math.max(probability, 1e-10)), // Prevent log(0)
                            logImpact: Math.log10(x)
                        });
                    }
                }
                
                categories[categoryId] = {
                    name: category.name,
                    color: category.color || '#3b82f6',
                    data: dataPoints,
                    params: { k, alpha }
                };
            } catch (error) {
                console.error(`Error creating power law distribution for category ${categoryId}:`, error);
            }
        });
        
        return categories;
    }, []);

    // Process actual simulation data for comparison
    const actualEventData = useMemo(() => {
        if (!simulationResults?.extremeEventResults) return [];
        
        return simulationResults.extremeEventResults.map((result, index) => {
            try {
                const event = result.extremeEvent;
                if (!event || !event.event) return null;
                
                const impact = 1 - (event.impactMultiplier || 1); // Convert to impact (0-1)
                
                // Extract NPV from indicators object (correct data structure)
                const originalNPV = result.originalResults?.indicators?.projectNPV || 0;
                const impactedNPV = result.impactedResults?.indicators?.projectNPV || 0;
                const npvLoss = originalNPV - impactedNPV;
                
                // Debug logging to check values
                if (index < 3) { // Log first few entries for debugging
                    console.log(`Event ${index}:`, {
                        originalNPV,
                        impactedNPV, 
                        npvLoss,
                        impactMultiplier: event.impactMultiplier
                    });
                }
                
                return {
                    id: index,
                    categoryId: event.categoryId || 'UNKNOWN',
                    eventName: event.event.name || 'Unknown Event',
                    impactMultiplier: event.impactMultiplier || 1,
                    impact: Math.max(0, impact * 100), // As percentage, ensure non-negative
                    npvLoss: Math.max(0, npvLoss), // Ensure non-negative
                    probability: event.probability || 0,
                    scenario: result.scenario || index + 1
                };
            } catch (error) {
                console.warn(`Error processing extreme event result ${index}:`, error);
                return null;
            }
        }).filter(item => item !== null); // Remove null entries
    }, [simulationResults]);

    const getCategoryColor = (categoryId) => {
        const colors = {
            FINANCIAL_RISK: '#ef4444',     // Red
            STRATEGIC_RISK: '#f97316',     // Orange  
            HAZARD_RISK: '#eab308',        // Yellow
            OPERATIONS_RISK: '#22c55e',    // Green
            EXTREME_BLACK_SWAN: '#8b5cf6', // Purple
            COMPOUND: '#ec4899',           // Pink
            CASCADE: '#06b6d4'             // Cyan
        };
        return colors[categoryId] || '#6b7280';
    };

    const renderPowerLawCurves = () => {
        const selectedData = selectedCategory === 'all' 
            ? Object.values(powerLawData).map(cat => cat.data).flat()
            : powerLawData[selectedCategory]?.data || [];

        if (selectedData.length === 0) return null;

        return (
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        Power Law Distribution Curves
                    </h3>
                    <div className="flex items-center space-x-4">
                        <select
                            value={chartScale}
                            onChange={(e) => setChartScale(e.target.value)}
                            className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-md 
                                     bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                        >
                            <option value="linear">Linear Scale</option>
                            <option value="log">Log-Log Scale</option>
                        </select>
                    </div>
                </div>
                
                <ResponsiveContainer width="100%" height={400}>
                    {selectedCategory === 'all' ? (
                        <LineChart>
                            <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? '#374151' : '#e5e7eb'} />
                            <XAxis 
                                dataKey={chartScale === 'log' ? 'logImpact' : 'impactMultiplier'}
                                stroke={isDarkMode ? '#9ca3af' : '#6b7280'}
                                label={{ 
                                    value: chartScale === 'log' ? 'Log(Impact Multiplier)' : 'Impact Multiplier', 
                                    position: 'insideBottom', 
                                    offset: -10 
                                }}
                                tickFormatter={(value) => {
                                    if (chartScale === 'log') {
                                        return value.toFixed(2);
                                    } else {
                                        return Number(value).toFixed(2);
                                    }
                                }}
                            />
                            <YAxis 
                                dataKey={chartScale === 'log' ? 'logProbability' : 'probability'}
                                stroke={isDarkMode ? '#9ca3af' : '#6b7280'}
                                label={{ 
                                    value: chartScale === 'log' ? 'Log(Probability)' : 'Probability', 
                                    angle: -90, 
                                    position: 'insideLeft' 
                                }}
                            />
                            <Tooltip 
                                formatter={(value, name) => [
                                    chartScale === 'log' ? `10^${value.toFixed(3)}` : value.toFixed(6),
                                    name
                                ]}
                                contentStyle={{
                                    backgroundColor: isDarkMode ? '#1f2937' : '#ffffff',
                                    border: isDarkMode ? '1px solid #374151' : '1px solid #e5e7eb',
                                    borderRadius: '6px'
                                }}
                            />
                            <Legend />
                            {Object.entries(powerLawData).map(([categoryId, category]) => (
                                <Line 
                                    key={categoryId}
                                    data={category.data}
                                    type="monotone" 
                                    dataKey={chartScale === 'log' ? 'logProbability' : 'probability'}
                                    stroke={getCategoryColor(categoryId)}
                                    strokeWidth={2}
                                    dot={false}
                                    name={category.name}
                                />
                            ))}
                        </LineChart>
                    ) : (
                        <LineChart data={selectedData}>
                            <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? '#374151' : '#e5e7eb'} />
                            <XAxis 
                                dataKey={chartScale === 'log' ? 'logImpact' : 'impactMultiplier'}
                                stroke={isDarkMode ? '#9ca3af' : '#6b7280'}
                                tickFormatter={(value) => {
                                    if (chartScale === 'log') {
                                        return value.toFixed(2);
                                    } else {
                                        return Number(value).toFixed(2);
                                    }
                                }}
                            />
                            <YAxis 
                                dataKey={chartScale === 'log' ? 'logProbability' : 'probability'}
                                stroke={isDarkMode ? '#9ca3af' : '#6b7280'}
                            />
                            <Tooltip 
                                formatter={(value, name) => [
                                    chartScale === 'log' ? `10^${value.toFixed(3)}` : value.toFixed(6),
                                    name
                                ]}
                                contentStyle={{
                                    backgroundColor: isDarkMode ? '#1f2937' : '#ffffff',
                                    border: isDarkMode ? '1px solid #374151' : '1px solid #e5e7eb',
                                    borderRadius: '6px'
                                }}
                            />
                            <Line 
                                type="monotone" 
                                dataKey={chartScale === 'log' ? 'logProbability' : 'probability'}
                                stroke={getCategoryColor(selectedCategory)}
                                strokeWidth={3}
                                dot={false}
                                name="Probability Density"
                            />
                            <Line 
                                type="monotone" 
                                dataKey="cumulativeProbability"
                                stroke={getCategoryColor(selectedCategory)}
                                strokeWidth={2}
                                strokeDasharray="5 5"
                                dot={false}
                                name="Cumulative Probability"
                            />
                        </LineChart>
                    )}
                </ResponsiveContainer>
                
                {selectedCategory !== 'all' && powerLawData[selectedCategory] && (
                    <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                        <div className="text-sm">
                            <span className="font-semibold">Power Law Parameters:</span>
                            <span className="ml-2">k = {powerLawData[selectedCategory].params.k}</span>
                            <span className="ml-4">α = {powerLawData[selectedCategory].params.alpha}</span>
                        </div>
                        <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                            P(x) = {powerLawData[selectedCategory].params.k} × x^(-{powerLawData[selectedCategory].params.alpha})
                        </div>
                    </div>
                )}
            </div>
        );
    };

    const renderProbabilityImpactScatter = () => {
        if (actualEventData.length === 0) return null;

        const filteredData = selectedCategory === 'all' 
            ? actualEventData 
            : actualEventData.filter(d => d.categoryId === selectedCategory);

        return (
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    Actual Events: Probability vs Impact Analysis
                </h3>
                
                <ResponsiveContainer width="100%" height={400}>
                    <ScatterChart>
                        <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? '#374151' : '#e5e7eb'} />
                        <XAxis 
                            dataKey="impact"
                            type="number"
                            domain={[0, 100]}
                            stroke={isDarkMode ? '#9ca3af' : '#6b7280'}
                            label={{ value: 'Impact (%)', position: 'insideBottom', offset: -10 }}
                        />
                        <YAxis 
                            dataKey="probability"
                            type="number"
                            stroke={isDarkMode ? '#9ca3af' : '#6b7280'}
                            label={{ value: 'Annual Probability', angle: -90, position: 'insideLeft' }}
                        />
                        <Tooltip 
                            formatter={(value, name, props) => {
                                const data = props.payload;
                                return [
                                    <div key="tooltip">
                                        <div><strong>{data.eventName}</strong></div>
                                        <div>Impact: {formatPercentage(data.impact)}</div>
                                        <div>Probability: {data.probability.toFixed(6)}</div>
                                        <div>NPV Loss: {formatCurrency(data.npvLoss)}</div>
                                        <div>Scenario: #{data.scenario}</div>
                                    </div>
                                ];
                            }}
                            contentStyle={{
                                backgroundColor: isDarkMode ? '#1f2937' : '#ffffff',
                                border: isDarkMode ? '1px solid #374151' : '1px solid #e5e7eb',
                                borderRadius: '6px'
                            }}
                        />
                        {selectedCategory === 'all' ? (
                            // Show all categories with different colors
                            Object.entries(RISK_CATEGORIES).map(([categoryId]) => (
                                <Scatter
                                    key={categoryId}
                                    name={RISK_CATEGORIES[categoryId]?.name || categoryId}
                                    data={actualEventData.filter(d => d.categoryId === categoryId)}
                                    fill={getCategoryColor(categoryId)}
                                />
                            ))
                        ) : (
                            <Scatter
                                name="Events"
                                data={filteredData}
                                fill={getCategoryColor(selectedCategory)}
                            />
                        )}
                        
                        {/* Risk quadrant lines */}
                        <ReferenceLine x={20} stroke="#ef4444" strokeDasharray="2 2" />
                        <ReferenceLine y={0.01} stroke="#ef4444" strokeDasharray="2 2" />
                    </ScatterChart>
                </ResponsiveContainer>
                
                <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div className="p-2 bg-red-50 dark:bg-red-900/20 rounded">
                        <div className="font-semibold text-red-800 dark:text-red-200">High Impact, High Probability</div>
                        <div className="text-red-600 dark:text-red-300">Critical risks requiring immediate attention</div>
                    </div>
                    <div className="p-2 bg-orange-50 dark:bg-orange-900/20 rounded">
                        <div className="font-semibold text-orange-800 dark:text-orange-200">High Impact, Low Probability</div>
                        <div className="text-orange-600 dark:text-orange-300">Extreme events requiring contingency planning</div>
                    </div>
                    <div className="p-2 bg-yellow-50 dark:bg-yellow-900/20 rounded">
                        <div className="font-semibold text-yellow-800 dark:text-yellow-200">Low Impact, High Probability</div>
                        <div className="text-yellow-600 dark:text-yellow-300">Operational issues requiring monitoring</div>
                    </div>
                    <div className="p-2 bg-green-50 dark:bg-green-900/20 rounded">
                        <div className="font-semibold text-green-800 dark:text-green-200">Low Impact, Low Probability</div>
                        <div className="text-green-600 dark:text-green-300">Manageable risks within tolerance</div>
                    </div>
                </div>
            </div>
        );
    };

    const renderDistributionComparison = () => {
        if (!simulationResults?.distributions) return null;

        const projectNPVData = [];
        if (simulationResults.distributions.projectNPV) {
            const dist = simulationResults.distributions.projectNPV;
            projectNPVData.push(
                { label: 'Mean', value: dist.mean, type: 'mean' },
                { label: 'Median', value: dist.median, type: 'median' },
                { label: '5th Percentile', value: dist.p5, type: 'risk' },
                { label: '25th Percentile', value: dist.p25, type: 'normal' },
                { label: '75th Percentile', value: dist.p75, type: 'normal' },
                { label: '95th Percentile', value: dist.p95, type: 'optimistic' }
            );
        }

        return (
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    Distribution Statistics Comparison
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Project NPV Distribution */}
                    <div>
                        <h4 className="font-medium text-gray-800 dark:text-gray-200 mb-3">Project NPV Distribution</h4>
                        <div className="space-y-2">
                            {projectNPVData.map((item, index) => (
                                <div key={index} className="flex justify-between items-center py-1">
                                    <span className="text-sm text-gray-600 dark:text-gray-400">{item.label}</span>
                                    <span className={`text-sm font-medium ${
                                        item.type === 'risk' ? 'text-red-600 dark:text-red-400' :
                                        item.type === 'optimistic' ? 'text-green-600 dark:text-green-400' :
                                        'text-gray-800 dark:text-gray-200'
                                    }`}>
                                        {formatCurrency(item.value)}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Extreme Event Statistics */}
                    {simulationResults.extremeEventStatistics && (
                        <div>
                            <h4 className="font-medium text-gray-800 dark:text-gray-200 mb-3">Extreme Event Impact</h4>
                            <div className="space-y-2">
                                <div className="flex justify-between items-center py-1">
                                    <span className="text-sm text-gray-600 dark:text-gray-400">Event Rate</span>
                                    <span className="text-sm font-medium text-orange-600 dark:text-orange-400">
                                        {formatPercentage(simulationResults.extremeEventStatistics.extremeEventRate * 100)}
                                    </span>
                                </div>
                                <div className="flex justify-between items-center py-1">
                                    <span className="text-sm text-gray-600 dark:text-gray-400">Average Impact</span>
                                    <span className="text-sm font-medium text-red-600 dark:text-red-400">
                                        {formatPercentage(simulationResults.extremeEventStatistics.averageExtremeEventImpact * 100)}
                                    </span>
                                </div>
                                <div className="flex justify-between items-center py-1">
                                    <span className="text-sm text-gray-600 dark:text-gray-400">Worst Case Scenario</span>
                                    <span className="text-sm font-medium text-red-800 dark:text-red-300">
                                        {formatCurrency(simulationResults.extremeEventStatistics.worstCaseScenario)}
                                    </span>
                                </div>
                                {simulationResults.extremeEventStatistics.valueAtRisk && (
                                    <>
                                        <div className="flex justify-between items-center py-1">
                                            <span className="text-sm text-gray-600 dark:text-gray-400">VaR 95%</span>
                                            <span className="text-sm font-medium text-red-600 dark:text-red-400">
                                                {formatCurrency(simulationResults.extremeEventStatistics.valueAtRisk.var95)}
                                            </span>
                                        </div>
                                        <div className="flex justify-between items-center py-1">
                                            <span className="text-sm text-gray-600 dark:text-gray-400">VaR 99%</span>
                                            <span className="text-sm font-medium text-red-800 dark:text-red-300">
                                                {formatCurrency(simulationResults.extremeEventStatistics.valueAtRisk.var99)}
                                            </span>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    return (
        <div className="space-y-6">
            {/* Category Selection */}
            <div className="flex flex-wrap items-center gap-2 mb-6">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Filter by Category:</span>
                {[
                    { key: 'all', label: 'All Categories' },
                    ...Object.entries(RISK_CATEGORIES).map(([key, category]) => ({
                        key,
                        label: category.name
                    }))
                ].map(category => (
                    <button
                        key={category.key}
                        onClick={() => setSelectedCategory(category.key)}
                        className={`px-3 py-1 rounded-full text-sm border transition-colors ${
                            selectedCategory === category.key
                                ? 'bg-blue-500 text-white border-blue-500'
                                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-200 dark:hover:bg-gray-600'
                        }`}
                    >
                        {category.label}
                    </button>
                ))}
            </div>

            {/* Power Law Distribution Curves */}
            {renderPowerLawCurves()}

            {/* Probability vs Impact Scatter Plot */}
            {renderProbabilityImpactScatter()}

            {/* Distribution Statistics */}
            {renderDistributionComparison()}
        </div>
    );
};

export default PowerLawChartsSubtab;